import Tooltip from "components/Tooltip/Tooltip";
import { getLocalTimezoneDate } from "config/ts-utils";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useMemo } from "react";
import ReactTimeAgo from "timeago-react";

interface TimeAgoProps {
    className?: string;
    datetime: string;
    hideTooltip?: boolean;
    /**
     * Seconds floor on the auto-refresh timer (timeago.js `minInterval`). Defaults
     * to 60 so a recently-changed label refreshes at most once per minute rather
     * than the library default of once per second — see DEFAULT_MIN_INTERVAL below.
     * Pass a smaller value on a BOUNDED/windowed list (e.g. the Logs tab, where only
     * the near-viewport rows are mounted) that wants a live seconds-granularity
     * "X ago" without reintroducing idle churn, since the mounted-row count is
     * capped there.
     */
    minInterval?: number;
}

/**
 * Throttle the auto-updating "X ago" label so it refreshes at most once per
 * minute instead of the library default of once per second for recently-changed
 * timestamps. `minInterval` (seconds) raises the floor on timeago.js's internal
 * timer; its natural coarsening for older times (≥1 min → minute cadence, etc.)
 * is unchanged. This eliminates the per-second DOM churn that dominates idle CPU
 * on dense live screens (the operator dashboard renders one TimeAgo per row plus
 * one per live widget), matching the refresh cadence to how fast a relative-time
 * label actually changes. Callers on a bounded/windowed list may lower it via the
 * `minInterval` prop so a seconds-old label still ticks while the user watches it
 * statically (Stefan, 2026-10-15, Logs tab feedback).
 */
const DEFAULT_MIN_INTERVAL = 60;

export const TimeAgo = ({
    className,
    datetime,
    hideTooltip,
    minInterval = DEFAULT_MIN_INTERVAL,
}: TimeAgoProps) => {
    const localTimezoneDate = getLocalTimezoneDate(datetime);
    // Memoise the opts object so timeago-react does not reset its internal timer on
    // every parent re-render (a fresh object identity would).
    const opts = useMemo(() => ({ minInterval }), [minInterval]);

    return hideTooltip ? (
        <ReactTimeAgo className={className} datetime={datetime} opts={opts} />
    ) : (
        <Tooltip
            overlay={localTimezoneDate}
            overlayStyle={{ zIndex: 3000 }}
            overlayClassName="light overlapText"
            triggerClassName="overlapTextTrigger"
            placement="bottomLeft"
            align={{ points: ["cc", "cc"], offset: [0, 0] }}
        >
            <ReactTimeAgo className={className} datetime={datetime} opts={opts} />
        </Tooltip>
    );
};

export const TimeLeft = ({ datetime, hideTooltip }: TimeAgoProps) => {
    const localTimezoneDate = getLocalTimezoneDate(datetime);
    dayjs.extend(relativeTime);
    const time = dayjs(datetime).endOf("hour").fromNow();

    return hideTooltip ? (
        <span>{time}</span>
    ) : (
        <Tooltip
            overlay={localTimezoneDate}
            overlayStyle={{ zIndex: 3000 }}
            overlayClassName="light overlapText"
            triggerClassName="overlapTextTrigger"
            placement="bottomLeft"
            align={{ points: ["cc", "cc"], offset: [0, 0] }}
        >
            {time}
        </Tooltip>
    );
};
